import React from 'react';
import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { Plugin } from 'prosemirror-state';
import { findParentNode } from 'prosemirror-utils';

import { BaseExtension, EditorOptions, extensionType } from '@lyearn/lygo-core';

import { PopperProps } from '../../types';
import {
  calculateOffset,
  calculateTransformOrigin,
  getDomNode,
  removeMenuContainer,
  run,
  setPluginMeta,
} from '../../utils';
import { linkMenuKey } from '../LinkMenu/utils';
import { AddMenuContainer } from './AddMenuContainer';
import { AddMenuPluginKey, OPEN_REGEX } from './consts';
import { addMenuProps } from './types';
import { getAddMenuDefaultProps, hideAddMenu } from './utils';

const ADD_MENU_CONTAINER_ID = 'add-menu-container';

export default class AddMenu extends BaseExtension {
  get type(): string {
    return extensionType.toolbar;
  }

  get name(): string {
    return 'add_menu';
  }

  plugins({ portalApi, isMicroLearning, isByteContent }: EditorOptions) {
    return [
      new Plugin({
        key: AddMenuPluginKey,
        state: {
          init() {
            return {
              open: false,
              anchorEl: null,
              placement: document.dir === 'ltr' ? 'top-start' : 'top-end',
              addMenuBlocks: [],
            };
          },
          apply(tr, old) {
            const props = tr.getMeta(AddMenuPluginKey);
            return props || old;
          },
        },
        view: (editorView) => {
          // This will be called only once. It will add a container div.
          // Add Menu will be rendered in this container div.
          let addMenuContainer = document.createElement('div');
          addMenuContainer.id = ADD_MENU_CONTAINER_ID;
          editorView?.dom?.parentNode?.appendChild(addMenuContainer);

          const portalKey = nanoid();
          // Need to return the following functions in the view function
          return {
            update: (view) => {
              const state = view.state;
              const props = AddMenuPluginKey.getState(state);

              const dom = editorView?.dom?.parentNode?.querySelector(`#${ADD_MENU_CONTAINER_ID}`);
              if (dom) {
                portalApi.createPortal(
                  ReactDOM.createPortal(
                    <AddMenuContainer
                      isByteContent={isByteContent}
                      isMicroLearning={isMicroLearning}
                      {...props}
                      blocks={props.addMenuBlocks}
                    />,
                    dom,
                    portalKey,
                  ),
                );
              }
            },
            destroy: () => {
              portalApi.destroyPortal(portalKey);
              removeMenuContainer(ADD_MENU_CONTAINER_ID, editorView);
            },
          };
        },

        props: {
          handleDOMEvents: {
            keydown: (editorView, event) => {
              const prevProps = AddMenuPluginKey.getState(editorView.state);
              // Ignore this keypresses here. It will be handled in the
              // AddMenu component.
              if (
                prevProps.open === true &&
                (event.key === 'ArrowUp' ||
                  event.key === 'ArrowDown' ||
                  event.key === 'Escape' ||
                  event.key === 'Enter')
              ) {
                return true;
              }

              const { pos: selectedPos } = editorView.state.selection.$from;
              // If not the match, hide the add menu immediately. This case is for
              // the time when it's opened by the plus button. Else user will see
              // the glitch. Don't return from here as it will be judge on the previous
              // string presented. New key-pressed character will not be inserted yet.
              if (prevProps.open === true) {
                const isMatch = run(
                  editorView,
                  selectedPos,
                  selectedPos,
                  OPEN_REGEX,
                  (_state: any, match: any) => {
                    return !!match;
                  },
                );
                if (!isMatch) {
                  hideAddMenu(editorView);
                }
              }

              // timeout ensures that the add/delete has been handled by prosemirror
              // and any characters removed, before we evaluate the rule.
              setTimeout(() => {
                const { pos } = editorView.state.selection.$from;
                const disabledNodesForMenu = [editorView.state.schema.nodes.toggle_block_heading];

                if (
                  findParentNode((node) => disabledNodesForMenu.includes(node.type))(
                    editorView.state.selection,
                  )
                ) {
                  return false;
                }

                return run(editorView, pos, pos, OPEN_REGEX, (_state: any, match: any) => {
                  let props = getAddMenuDefaultProps() as addMenuProps;
                  const linkMenuprops = linkMenuKey.getState(editorView.state);
                  if (match && !linkMenuprops?.open) {
                    const cursorPos = editorView.state.selection.$to.pos;
                    const coordsPos = editorView.coordsAtPos(cursorPos) as any;
                    const domNode = getDomNode(editorView, cursorPos);
                    if (prevProps.open === false) {
                      // @ts-ignore
                      const getBoundingClientRect = domNode.getBoundingClientRect();
                      props.offset = calculateOffset(coordsPos, getBoundingClientRect);
                      props.placement = calculateTransformOrigin(coordsPos);
                    } else {
                      props.offset = prevProps.offset;
                      props.placement = prevProps.placement;
                    }
                    props.open = true;
                    props.editorView = editorView;
                    props.anchorEl = domNode as PopperProps['anchorEl'];
                    props.match = match[1];
                  }

                  setPluginMeta(editorView, AddMenuPluginKey, { ...prevProps, ...props });
                  return null;
                });
              });

              return false;
            },
          },
        },
      }),
    ];
  }
}
